import filter from 'lodash/filter'
import map from 'lodash/map'
import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import { SWRConfig } from 'swr'

import { contentstackLoader } from '@Components/Image'
import { BlockConfigContext } from '@Globals/CMS/Context'
import { DataLayer } from '@Globals/Google/DataLayer'
import { PageProps } from '@Globals/Page/Types'

export const PageHOC = <T extends PageProps>(Component: NextPage<T>): NextPage<T> => (props) => {
  const {
    alternateLinks = [],
    blockConfig,
    openGraph,
    page,
    seo,
    swrFallback,
  } = props

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { locales } = useRouter()

  const filteredAlternateLinks = filter(
    alternateLinks,
    (link) => {
      if (locales && locales.includes(link.locale) === false) {
        return false
      }

      return true
    },
  )
  return (
    <>
      <Head>
        {/* SEO */}
        {seo?.title && <title>{seo.title}</title>}
        {seo?.description && <meta key="description" content={seo.description} name="description" />}
        <meta key="robots" content={seo?.noIndex === true ? 'noindex, follow' : 'index, follow'} name="robots" />
        {page.url && <link key="canonical" href={page.url} rel="canonical" />}

        {/* Open Graph */}
        {openGraph?.description && <meta key="og:description" content={openGraph.description} property="og:description" />}
        {openGraph?.image && <meta key="og:image" content={contentstackLoader({ src: openGraph.image, width: 1200 })} property="og:image" />}
        {openGraph?.siteName && <meta key="og:site_name" content={openGraph.siteName} property="og:site_name" />}
        {openGraph?.title && <meta key="og:title" content={openGraph.title} property="og:title" />}
        {openGraph?.type && <meta key="og:type" content={openGraph.type} property="og:type" />}
        {page.url && <meta key="og:url" content={page.url} property="og:url" />}

        {/* Alternate Links */}
        {map(filteredAlternateLinks, item => (
          <link
            key={item.locale}
            href={item.url}
            hrefLang={item.locale}
            rel="alternate"
          />
        ))}
      </Head>
      {/* Render component */}
      <SWRConfig value={{ fallback: swrFallback }}>
        <BlockConfigContext.Provider value={blockConfig}>
          <Component {...props} />
        </BlockConfigContext.Provider>
      </SWRConfig>

      {/* Tracking */}
      <DataLayer input={{ contentIds: page.id, pageType: page.type, type: page.type }} />
    </>
  )
}

